import { ICS2_LEVEL_INDEX, Ics2ErrorLocation } from './types';

const MASTER_SEGMENT = 'consignmentMasterLevel';
const HOUSE_SEGMENT = 'consignmentHouseLevel';
const GOODS_ITEM_SEGMENT = 'goodsItem';

/** The filing the error answers roots the path: IE3F11, IE3F50, IE3A10, ... */
const MESSAGE_ROOT = /^IE\d[A-Z]\d+$/i;

/** Declaration-level identifiers customs names in upper case; the form has no field for them. */
const IDENTIFIER_SEGMENTS = new Set(['LRN', 'MRN']);

const ENS_PAYLOAD_FIELDS = new Set([
    'declarant',
    'representative',
    'customsOfficeOfFirstEntry',
    'activeBorderTransportMeans',
    'documentIssueDate',
    'specificCircumstanceIndicator',
    'reEntryIndicator',
    'splitConsignment',
]);

interface PathSegment {
    name: string;
    index?: number;
}

/** `transportEquipment[0]` -> `{ name: 'transportEquipment', index: 0 }`. */
const toSegment = (raw: string): PathSegment => {
    const match = raw.match(/^(.*?)\[(\d+)\]$/);

    return match ? { name: match[1], index: Number(match[2]) } : { name: raw };
};

const toDottedPath = (segments: PathSegment[]): string =>
    segments.flatMap(({ name, index }) => (index === undefined ? [name] : [name, String(index)])).join('.');

/** A missing index means the class occurs once, which is the first and only entry. */
const toDisplayIndex = (index?: number): number => (index ?? 0) + 1;

/** The segments of a path, without the message root it may start with. */
const toPathSegments = (messageElementPath?: string): PathSegment[] => {
    const segments = (messageElementPath ?? '').trim().split('/').filter(Boolean).map(toSegment);

    if (segments.length && MESSAGE_ROOT.test(segments[0].name)) segments.shift();

    return segments;
};

/**
 * Turns an ICS2 `pointer/messageElementPath` into a location.
 *
 * Returns undefined for anything that does not look like a path into the filing, so callers can
 * fall back to showing the raw string rather than guessing at a wrong field.
 */
export const parseIcs2MessageElementPath = (messageElementPath?: string): Ics2ErrorLocation | undefined => {
    const segments = toPathSegments(messageElementPath);
    if (!segments.length) return undefined;

    // `/LRN` and `/MRN` identify the filing rather than a field anyone can edit.
    if (segments.length === 1 && IDENTIFIER_SEGMENTS.has(segments[0].name)) {
        return { level: 'master', itemIndex: ICS2_LEVEL_INDEX };
    }

    if (segments[0].name !== MASTER_SEGMENT) return undefined;

    const houseAt = segments.findIndex(({ name }) => name === HOUSE_SEGMENT);
    const itemAt = segments.findIndex(({ name }) => name === GOODS_ITEM_SEGMENT);

    const houseConsignmentIndex = houseAt === -1 ? undefined : toDisplayIndex(segments[houseAt].index);
    const itemIndex = itemAt === -1 ? undefined : toDisplayIndex(segments[itemAt].index);

    // The master form owns the master level, so its paths keep the `consignmentMasterLevel`
    // prefix. An item or a house consignment is edited by its own form, relative to itself.
    if (itemAt !== -1) {
        const fieldPath = toDottedPath(segments.slice(itemAt + 1));

        return houseAt === -1
            ? { level: 'masterItem', itemIndex, fieldPath }
            : { level: 'houseItem', houseConsignmentIndex, itemIndex, fieldPath };
    }

    if (houseAt !== -1) {
        return {
            level: 'houseConsignment',
            houseConsignmentIndex,
            itemIndex: ICS2_LEVEL_INDEX,
            fieldPath: toDottedPath(segments.slice(houseAt + 1)),
        };
    }

    return { level: 'master', itemIndex: ICS2_LEVEL_INDEX, fieldPath: toDottedPath(segments) };
};

/**
 * The same, for a pointer into an ENS. These fields sit directly on the ENS payload, beside the master
 * level, and the master form edits them under the same names. It is kept apart from the parser above
 * because an arrival notification has an `activeBorderTransportMeans` of its own and no master form.
 */
export const parseIcs2EnsMessageElementPath = (messageElementPath?: string): Ics2ErrorLocation | undefined => {
    const segments = toPathSegments(messageElementPath);

    if (segments.length && ENS_PAYLOAD_FIELDS.has(segments[0].name)) {
        return { level: 'master', itemIndex: ICS2_LEVEL_INDEX, fieldPath: toDottedPath(segments) };
    }

    return parseIcs2MessageElementPath(messageElementPath);
};
